import { ComponentProps } from "react";

import { AvailableIntegrationFragment } from "@/apollo/types";
import { FeatureGuard } from "@/components/elements/FeatureGuard";
import { Heading, TextMuted } from "@/components/elements/Typography";
import { UpgradeNowButton } from "@/components/modules/UpgradeNowButton";
import cn from "@/helpers/classNames";
import { isCustomConnector } from "@/integrations";

type CustomConnector = Pick<
  AvailableIntegrationFragment,
  "id" | "meetsPlanRequirement"
>;

export function useCustomConnectorAccess(integration: CustomConnector) {
  return { isEnabled: integration.meetsPlanRequirement };
}

export function CustomConnectorAccessGuard(
  props: {
    integration: CustomConnector;
    children: React.ReactNode;
  } & Pick<ComponentProps<typeof FeatureGuard>, "fallback">,
) {
  const { isEnabled } = useCustomConnectorAccess(props.integration);
  return (
    <FeatureGuard
      test={!isCustomConnector(props.integration.id) || isEnabled}
      fallback={props.fallback}
    >
      {props.children}
    </FeatureGuard>
  );
}

export function CustomConnectorTeaser(props: ComponentProps<"div">) {
  return (
    <div {...props} className={cn("flex flex-col gap-4", props.className)}>
      <Heading className="text-xl">Connect to any data source</Heading>
      <TextMuted className="text-sm leading-snug">
        Custom Connectors let you create and host your own connections to data
        sources or APIs. Weld handles the rest - scheduling, triggering,
        schemas, and data loading.{" "}
        <a
          href="https://weld.app/docs/custom-connector"
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 underline"
        >
          Learn more
        </a>{" "}
        about Custom Connectors
      </TextMuted>
      <div className="flex w-full flex-col gap-4 text-center">
        <span>The Custom Connector is available on the Premium plan.</span>
        <UpgradeNowButton
          source="feature guard - custom connector"
          className="w-full"
        />
      </div>
    </div>
  );
}
